import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiLoader, FiAlertTriangle, FiArrowLeft, FiList, FiCpu } from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { supabase } from '@/integrations/supabase/client';
import AdminTasksPanel from '../components/admin/AdminTasksPanel';
import CabBotReportsPanel from '../components/admin/CabBotReportsPanel';

const CTOTasksDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { isAdmin, loading: authLoading } = useSubscription();
  const [isCTOManager, setIsCTOManager] = useState(false);
  const [checkingAccess, setCheckingAccess] = useState(true);
  const [activeView, setActiveView] = useState<'tasks' | 'cab-bot'>('tasks');

  useEffect(() => {
    const checkCTOAccess = async () => {
      if (!user) {
        setCheckingAccess(false);
        return;
      }

      try {
        const { data, error } = await supabase.rpc('is_cto_manager');
        if (!error && data === true) {
          setIsCTOManager(true);
        }
      } catch (err) {
        console.error('Error checking CTO access:', err);
      }
      setCheckingAccess(false);
    };

    if (!authLoading) {
      checkCTOAccess();
    }
  }, [user, authLoading]);

  // Loading state
  if (authLoading || checkingAccess) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-900 text-white">
        <FiLoader className="animate-spin text-purple-500 text-5xl mb-4" />
        <p className="text-slate-400">Checking access...</p>
      </div>
    );
  }

  // Access denied
  if (!isAdmin && !isCTOManager) {
    return (
      <div className="flex flex-col items-center justify-center h-screen bg-slate-900 text-white p-8">
        <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-8 max-w-md text-center">
          <FiAlertTriangle className="text-red-400 text-5xl mx-auto mb-4" />
          <h1 className="text-xl font-bold text-red-300 mb-2">Access Denied</h1>
          <p className="text-slate-400 text-sm mb-4">
            You don't have permission to access CTO Tasks.
          </p>
          <p className="text-xs text-slate-500 mb-6">
            Connected as: {user?.email}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="inline-block bg-gradient-to-r from-purple-600 to-cyan-600 text-white py-2 px-6 rounded-xl font-medium"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 border-b border-slate-700/50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex items-center space-x-2 text-slate-400 hover:text-white transition-colors"
            >
              <FiArrowLeft />
              <span>Back to Dashboard</span>
            </button>
          </div>
          {/* View Tabs */}
          <div className="flex items-center gap-1 bg-slate-800 border border-slate-700/50 rounded-xl p-1">
            <button
              onClick={() => setActiveView('tasks')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
                activeView === 'tasks'
                  ? 'bg-purple-600/20 text-purple-300 border border-purple-500/30'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <FiList className="text-sm" />
              Tasks
            </button>
            <button
              onClick={() => setActiveView('cab-bot')}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm transition-all ${
                activeView === 'cab-bot'
                  ? 'bg-orange-600/20 text-orange-300 border border-orange-500/30'
                  : 'text-slate-400 hover:text-white'
              }`}
            >
              <FiCpu className="text-sm" />
              CAB Bot V2
            </button>
          </div>
          <div className="text-sm text-slate-400">
            {user?.email} • {isAdmin ? 'Admin' : 'CTO Manager'}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="p-6 max-w-7xl mx-auto">
        <motion.div
          key={activeView}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          {activeView === 'tasks' ? <AdminTasksPanel /> : <CabBotReportsPanel />}
        </motion.div>
      </main>
    </div>
  );
};

export default CTOTasksDashboard;
